import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Formik, Field } from 'formik'
import { useCustomer, useEmailIsAvailable } from 'lib/core/hooks'
import { Form } from 'lib/ui'
import { InterceptBlurEvent } from 'lib/ui/components/Form/types'
import { BLANK_SPACE } from 'lib/core/resources/constants'
import { useCheckout, useCheckoutContext, Login } from 'lib/modules/checkout'
import { schema, validation, Values } from './schema'
import { LoginContainer } from './styled'

interface Props {
  interceptBlur?: (e: InterceptBlurEvent<string>) => void
}

export const EmailForm = ({ interceptBlur }: Props) => {
  const intl = useIntl()
  const { setGuestEmail, checkoutSchema } = useCheckout()
  const { setState } = useCheckoutContext()
  const { isLoggedIn } = useCustomer()
  const { emailIsAvailable } = useEmailIsAvailable(checkoutSchema?.email)
  const [showInlineLoginForm, setShowInlineLoginForm] = useState(true)

  const initialValues = {
    ...schema,
    email: checkoutSchema?.email ?? ''
  }

  const handleSubmit = (values: Values) => {
    const { email } = values
    setGuestEmail(email, {
      debounce: true,
      callback: {
        onSuccess: () => {
          setState((state) => ({
            ...state,
            emailErrors: {}
          }))
        }
      }
    })
  }

  useEffect(() => {
    if (isLoggedIn) {
      setState(state => ({
        ...state,
        emailErrors: {}
      }))
    }
  }, [isLoggedIn, setState])

  if (isLoggedIn) {
    return null
  }

  if (!emailIsAvailable && showInlineLoginForm) {
    return <Login interceptBlur={interceptBlur} onCancel={() => setShowInlineLoginForm(false)} />
  }

  return (
    <>
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validation({ intl })}
      >
        <Form.Wrapper>
          <Form.FormikObserver<Values>
            autoSubmit
            onError={(errors) => {
              setState((state) => ({
                ...state,
                emailErrors: errors
              }))
            }}
          />

          <h2>
            <FormattedMessage id='checkout.general.title' />
          </h2>

          <Field
            name='email'
            label={intl.formatMessage({
              id: 'checkout.general.email.label.email'
            })}
            component={Form.Input}
            autoFocus
            interceptBlur={interceptBlur}
            isRequired
            hasAutoPlaceholder
          />
        </Form.Wrapper>
      </Formik>

      {!emailIsAvailable && (
        <LoginContainer>
          <FormattedMessage
            id='customer.account.login.hasAccountQuestion'
          />
          {BLANK_SPACE}
          <button
            onClick={() => setShowInlineLoginForm(true)}
            type='button'
          >
            <FormattedMessage
              id='action.login'
            />
          </button>
        </LoginContainer>
      )}
    </>
  )
}
